/**
 * SOLID Review
 * S — Strong. One concern: whose credits a balance is, and where it is kept — the account and the seated player —
 *     plus whose pushed balance to listen for.
 * O — Good. A new source of a balance is one more move over `changed`.
 * L — N/A — no inheritance hierarchy (private constructor).
 * I — Strong. The session part a decision reads, two moves and one question.
 * D — Strong. The account value object, the schemas' types and the builder's part role.
 */
import { Account } from '../session/Account';
import type { IStateClientSessionPart } from '../decisions/ClientDecision';
import type { IStateClientSession, IPropsClientSessionChanges, IEntityClientPlayer } from '../ports/schemas/ClientSession';
import type { ICommandCreditsChanged, IPayloadAccountWatch } from '../ports/schemas/PaymentMoves';

/**
 * A PLAYER'S BALANCE, AS A SMALL AGGREGATE (frontend-domain-oo P6, 2026-10-26; handbook §28.273) — the decide
 * functions of FE4c (`decisions/payment/credits.ts`, 2026-10-04), moved from AppRoot's push handler and
 * PaymentSuccessScreen's poll, which each wrote the auth store's credits (`updateMatchCredits`) and the game store's
 * seated player (`applyCreditsUpdated`). The account keeps the balance when it is the account's or the seated
 * player's; the seated player only when it is theirs (its tier follows, in the store). A balance for anyone else
 * changes nothing, whatever the server sent.
 */
export class Credits implements IStateClientSessionPart {
  private constructor(
    private account: Account,
    private player: IEntityClientPlayer | null,
  ) {}

  /** The credits the session holds: the account's and the seated player's. */
  static of(session: Pick<IStateClientSession, 'account' | 'player'>): Credits {
    return new Credits(Account.of(session.account), session.player);
  }

  /** A balance pushed or read for a player: kept where it is theirs — false if it is nobody's here. */
  changed(command: ICommandCreditsChanged): boolean {
    const player = this.player;
    const seated = player !== null && player.playerId === command.playerId;
    if (this.account.playerId !== command.playerId && !seated) return false;
    this.account = this.account.withCredits(command.credits);
    if (seated) this.player = { ...player, matchCredits: command.credits };
    return true;
  }

  /** The signed-in account's own balance, read back after a purchase — false when nobody is signed in. */
  balanceRead(credits: number): boolean {
    const { playerId } = this.account;
    if (playerId === null) return false;
    const own: ICommandCreditsChanged = { playerId, credits };
    return this.changed(own);
  }

  /** Whose pushed balance to hear: the seated player's (AppRoot's subscription key), or nobody's. */
  watched(): IPayloadAccountWatch {
    const target: IPayloadAccountWatch = { playerId: this.player?.playerId ?? null };
    return target;
  }

  sessionFields(): IPropsClientSessionChanges {
    const fields: IPropsClientSessionChanges = { account: this.account.toSnapshot(), player: this.player };
    return fields;
  }
}
